import { useState } from 'react'
import { Link } from 'react-router-dom'

import { FxButton, FxCreateTile, FxIcon } from '@/components/fx'

import { PageTitle } from '@/layouts/shellChrome'

/**
 * The first day: an account with no Musterformular yet.
 *
 * `StartUebersicht.dc.html` draws an "Erste Schritte" checklist with four
 * ticked-off steps, but every one of those ticks is a claim about a form
 * that does not exist yet ("46 Felder erkannt", "3 Felder unklar"). With no
 * template there is nothing to tick, so the checklist becomes what it can
 * honestly be: the way in, plus what will happen after it. Once a form
 * exists the ordinary overview takes over and the card carries the real
 * state.
 *
 * The questionnaire card ("Vier kurze Fragen") leads to `/fragen`, which is
 * a screen of its own in this build. It is the only way into it, so leaving
 * it out (as an earlier draft did) made a finished screen reachable by typing
 * its URL. "Nicht jetzt" hides it for this visit and nothing more: the
 * answers are optional, and a dismissal is not worth a record.
 */
const STEPS = [
  {
    icon: 'upload_file',
    title: 'Musterformular hochladen',
    body: 'Ein leeres oder ausgefülltes Exemplar Ihres Formulars als PDF, JPG oder PNG.',
  },
  {
    icon: 'stylus_note',
    title: 'Felder prüfen',
    body: 'Wir erkennen die Felder automatisch. Sie sehen sie und passen an, was nicht stimmt.',
  },
  {
    icon: 'assignment_turned_in',
    title: 'Dokumente auswerten',
    body: 'Jedes ausgefüllte Exemplar wird mit denselben Feldern gelesen — auch per Handyfoto.',
  },
  {
    icon: 'table_view',
    title: 'Als Tabelle exportieren',
    body: 'Alle Dokumente eines Formulars in einer Excel- oder CSV-Datei, eine Zeile je Dokument.',
  },
]

export function FirstDay({ firstName, onCreate }: { firstName: string; onCreate: () => void }) {
  const [questionsDismissed, setQuestionsDismissed] = useState(false)

  return (
    <div className="flex flex-col gap-8">
      <PageTitle>{firstName ? `Willkommen, ${firstName}!` : 'Willkommen!'}</PageTitle>
      <p className="max-w-[640px] text-body text-text">
        Laden Sie ein Musterformular hoch. Daraus lernt Formalogix, welche Felder es in Ihren
        ausgefüllten Dokumenten lesen soll.
      </p>

      <FxCreateTile onClick={onCreate} />

      <ol className="grid list-none grid-cols-1 gap-4 p-0 xs:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((step, index) => (
          <li
            key={step.title}
            className="flex min-w-0 flex-col gap-2 rounded-sm bg-surface p-4 shadow-raised"
          >
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary-subtle text-body-strong text-primary-text"
              >
                {index + 1}
              </span>
              <FxIcon name={step.icon} size={22} className="flex-none text-primary" />
              <b className="min-w-0 text-body-strong">{step.title}</b>
            </div>
            <span className="text-small text-text">{step.body}</span>
          </li>
        ))}
      </ol>

      {!questionsDismissed && (
        <section className="flex max-w-[640px] flex-col gap-3 rounded-sm bg-surface p-5 shadow-raised">
          <div className="flex items-center gap-3">
            <FxIcon name="tune" size={28} className="text-primary" />
            <b className="text-body-strong">Vier kurze Fragen</b>
          </div>
          <span className="text-small text-text">
            Womit arbeiten Sie, und was soll herauskommen? Damit richten wir Formalogix passend
            ein. Freiwillig.
          </span>
          <div className="flex items-center gap-4">
            <FxButton asChild variant="tonal">
              <Link to="/fragen">Beantworten</Link>
            </FxButton>
            <button
              type="button"
              className="text-small text-primary-text underline"
              onClick={() => setQuestionsDismissed(true)}
            >
              Nicht jetzt
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
